import {useCallback, useRef, useEffect, useState} from 'react'
import Tesseract from 'tesseract.js'
import {cropToCameraFrame} from '../../utils/labelCropper'
import {extractAddresses, extractBarcodeNumber} from '../../utils/ocrUtils'
import {MAX_STORAGE_SIZE} from '../../consts/storage'

const CONFIG = {
  OUTPUT_QUALITY: 0.85,
  MAX_OUTPUT_WIDTH: 1280,
}

export function useImageProcessor(hash: string | undefined) {
  const ocrWorkerRef = useRef<Tesseract.Worker | null>(null)
  const compressionCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [isWorkerReady, setIsWorkerReady] = useState(false)

  useEffect(() => {
    compressionCanvasRef.current = document.createElement('canvas')

    Tesseract.createWorker()
      .then((worker) => {
        ocrWorkerRef.current = worker
        setIsWorkerReady(true)
      })
      .catch(() => {
        setIsWorkerReady(false)
      })

    return () => {
      ocrWorkerRef.current?.terminate()
      ocrWorkerRef.current = null
      if (compressionCanvasRef.current) {
        compressionCanvasRef.current.width = 0
        compressionCanvasRef.current.height = 0
        compressionCanvasRef.current = null
      }
      setIsWorkerReady(false)
    }
  }, [])

  const compressImage = useCallback((canvas: HTMLCanvasElement): Promise<string | null> => {
    return new Promise((resolve) => {
      let outputWidth = canvas.width
      let outputHeight = canvas.height

      if (outputWidth > CONFIG.MAX_OUTPUT_WIDTH) {
        const ratio = CONFIG.MAX_OUTPUT_WIDTH / outputWidth
        outputWidth = CONFIG.MAX_OUTPUT_WIDTH
        outputHeight = Math.floor(canvas.height * ratio)
      }

      const outputCanvas = compressionCanvasRef.current
      if (!outputCanvas) {
        resolve(null)
        return
      }

      outputCanvas.width = outputWidth
      outputCanvas.height = outputHeight

      const ctx = outputCanvas.getContext('2d')
      if (!ctx) {
        resolve(null)
        return
      }

      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(canvas, 0, 0, outputWidth, outputHeight)

      outputCanvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null)
            return
          }
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.onerror = () => resolve(null)
          reader.readAsDataURL(blob)
        },
        'image/jpeg',
        CONFIG.OUTPUT_QUALITY
      )
    })
  }, [])

  const processAndStore = useCallback(
    async (canvas: HTMLCanvasElement): Promise<{success: boolean}> => {
      if (!hash || !isWorkerReady) return {success: false}

      try {
        const {canvas: croppedCanvas, confidence} = cropToCameraFrame(canvas)

        sessionStorage.setItem(`label_confidence_${hash}`, confidence)

        const addressResult = await extractAddresses(croppedCanvas, ocrWorkerRef.current)
        if (!addressResult.success) {
          return {success: false}
        }

        const barcodeResult = await extractBarcodeNumber(croppedCanvas, ocrWorkerRef.current)
        if (!barcodeResult.success) {
          return {success: false}
        }

        const ocrData = {
          from_street1: addressResult.from,
          to_street1: addressResult.to,
          barcode_number: barcodeResult.barcode,
          regions: {
            to: addressResult.regions.to,
            tracking: barcodeResult.region,
          },
        }

        sessionStorage.setItem(`label_ocr_${hash}`, JSON.stringify(ocrData))

        const imageBase64 = await compressImage(croppedCanvas)

        if (!imageBase64) return {success: false}

        // Validate base64 format to prevent XSS
        if (!/^data:image\/jpeg;base64,[A-Za-z0-9+/]+=*$/.test(imageBase64)) {
          return {success: false}
        }

        const sizeInBytes = Math.ceil((imageBase64.length * 3) / 4)
        if (sizeInBytes > MAX_STORAGE_SIZE) {
          return {success: false}
        }

        sessionStorage.setItem(`label_image_${hash}`, imageBase64)
        return {success: true}
      } catch {
        return {success: false}
      }
    },
    [hash, compressImage, isWorkerReady]
  )

  return {processAndStore, isWorkerReady}
}
